// src/components/books/StepQuizRunner.jsx
// ================================================================
// اجرای آزمون مرحله — اتصال بین TakeExam و progressManager
// ================================================================
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { useLessonData } from '../../hooks/useBooksEngine.js';
import { useExams } from '../Exams/ExamContext.jsx';
import { progressManager } from '../../core/books/logic.js';
import { engine } from '../../core/books/storage.js';
import { EmptyState } from './BooksSharedUI.jsx';
import TakeExam from './../Exams/TakeExam.jsx';

import { startPayment, PAYMENT_PURPOSE } from '../../services/paymentService.js';

import { StepQuizIntro, StepQuizResult, StepQuizPaywall } from './StepQuizUI.jsx';

// ================================================================
// ENTRY
// ================================================================
export default function StepQuizRunner() {
  const { grade, subject, folderName, lessonId, stepId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const lessonData = useLessonData(grade, subject, folderName, lessonId);

  if (!user) return <EmptyState icon="🔒" title="لطفاً وارد شوید" />;

  return (
    <RunnerInner
      user={user}
      lessonData={lessonData}
      {...{ grade, subject, folderName, lessonId, stepId, navigate }}
    />
  );
}

// ================================================================
// INNER
// ================================================================
function RunnerInner({
  user, lessonData, grade, subject, folderName, lessonId, stepId, navigate,
}) {
  const examCtx = useExams();
  const [tick, setTick] = useState(0);
 const [lastResult, setLastResult] = useState(null);
  // ──── quiz ────
  const quiz = useMemo(
    () => examCtx.getStepQuizByStepId(stepId),
    [stepId, examCtx.exams]
  );

  // ──── step ────
  const step = useMemo(() => {
    if (!lessonData?.lesson?.steps) return null;
    return lessonData.lesson.steps.find((s) => s._id === stepId) || null;
  }, [lessonData, stepId]);

  // ──── project (پایدار، برای کلید progress) ────
  const projectId = lessonData?.project?.id
    || lessonData?.project?.projectId
    || lessonData?.bookData?.projectId
    || null;

  // ──── گوش دادن به رویدادها ────
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    const unsub1 = engine.on('quiz:step-progress-changed', bump);
    const unsub2 = engine.on('quiz:unlocked', bump);
    const unsub3 = engine.on('quiz:step-passed', bump);
    return () => {
      try { unsub1(); } catch {}
      try { unsub2(); } catch {}
      try { unsub3(); } catch {}
    };
  }, []);

  // ──── progress (منبع واحد) ────
  const progress = useMemo(() => {
    if (!user?.id || !projectId || !stepId) return null;
    try {
      return progressManager.getQuizStepProgress(user.id, projectId, stepId);
    } catch { return null; }
  }, [user?.id, projectId, stepId, tick]);

  // ──── canAttempt ────
  const canAttempt = useMemo(() => {
    if (!user?.id || !projectId || !stepId || !quiz) {
      return { allowed: false, reason: 'not-ready', attemptsLeft: 0, totalAttempts: 0, retryPrice: 0 };
    }
    try {
      return progressManager.canAttemptQuiz(user.id, projectId, stepId, {
        maxAttempts: quiz.maxAttempts ?? 2,
        retryPrice: quiz.retryPrice ?? 10000,
      });
    } catch {
      return { allowed: false, reason: 'error', attemptsLeft: 0, totalAttempts: 0, retryPrice: 0 };
    }
  }, [user?.id, projectId, stepId, quiz, tick]);

  // ──── view sync ────
  const [view, setView] = useState('intro');
  useEffect(() => {
    if (!quiz) return;
    // ✅ اگر کاربر در حال آزمون دادن یا مشاهده‌ی نتیجه است، افکت به‌زور جای دیگری نبرد
    if (view === 'taking' || view === 'result') return;
    if (progress?.passed) {
      setView('result');
    } else if (!canAttempt.allowed && canAttempt.reason === 'payment-required') {
      setView('paywall');
    } else {
      setView('intro');
    }
  }, [quiz, progress?.passed, canAttempt.allowed, canAttempt.reason, view]);

  // ──── handlers ────
  const handleStartQuiz = useCallback(() => {
    setLastResult(null);   // 🆕 پاک کردن کارنامه قبلی
    setView('taking');
  }, []);

  const handleQuizSubmit = useCallback(async (result) => {
    const score = Number(result?.totalScore) || 0;
    const passThreshold = Number(quiz?.passThreshold) || 12;
    const passed = score >= passThreshold;

    // 🆕 ذخیره‌ی کارنامه‌ی کامل (شامل details) برای نمایش
    setLastResult({
      ...result,
      passed,
      score,
      passThreshold,
      submittedAt: Date.now(),
    });

    if (user?.id && projectId && stepId) {
      progressManager.recordQuizAttempt(user.id, projectId, stepId, {
        quizId: quiz?.id || null,
        score,
        passed,
        passThreshold,
      });

      if (passed && lessonData?.lesson) {
        try {
          const legacyLessonId = lessonData.lesson.legacyLessonId;
          const stepIndex = lessonData.lesson.steps.findIndex((s) => s._id === stepId);
          progressManager.addCompletedStep(
            user.id, grade, subject, folderName, legacyLessonId,
            stepId, stepIndex, lessonData.lesson
          );
        } catch (e) {
          console.warn('[StepQuizRunner] addCompletedStep failed:', e);
        }
      }
    }

    // 🆕 tick را هم افزایش بده تا useMemo progress recompute شود
    setTick((t) => t + 1);
    setView('result');
  }, [quiz, projectId, stepId, user?.id, lessonData, grade, subject, folderName]);

const handlePayAndUnlock = useCallback(async () => {
    // ── اعتبارسنجی ──
    if (!user?.id) {
      alert('برای پرداخت ابتدا وارد حساب خود شوید');
      return;
    }
    if (!projectId) {
      alert('اطلاعات پروژه ناقص است');
      return;
    }
    if (!stepId) {
      alert('اطلاعات مرحله ناقص است');
      return;
    }
    if (!quiz?.id) {
      alert('اطلاعات آزمون ناقص است');
      return;
    }

    const price = quiz?.retryPrice || 10000;
    const confirmed = window.confirm(
      `برای شرکت مجدد در این آزمون، مبلغ ${price.toLocaleString('fa-IR')} تومان پرداخت می‌شود.\nآیا مطمئن هستید؟`
    );
    if (!confirmed) return;

    // ── پرداخت واقعی ──
    try {
      await startPayment({
        userId: user.id,
        purpose: PAYMENT_PURPOSE.STEP_QUIZ_UNLOCK,
        metadata: {
          quizId: quiz.id,
          stepId: stepId,
          projectId: projectId,
          price: price,
        },
        description: 'آنلاک آزمون مرحله‌ای',
      });
      // کاربر به درگاه هدایت می‌شود؛ ادامه‌ی کار در PaymentCallbackPage انجام می‌گیرد
    } catch (err) {
      console.error('❌ پرداخت ناموفق:', err);
      alert(err?.message || 'خطا در شروع پرداخت. لطفاً دوباره تلاش کنید.');
    }
  }, [user?.id, projectId, stepId, quiz?.id, quiz?.retryPrice]);

  const handleRetry = useCallback(() => {
    setLastResult(null);   // 🆕 پاک کردن کارنامه قبلی
    setView('taking');
  }, []);

  // ──── Guards ────
  if (!lessonData) return <EmptyState icon="📚" title="درس یافت نشد" />;
  if (!quiz) return <EmptyState icon="🔍" title="آزمون یافت نشد" />;
  if (!step) return <EmptyState icon="🔍" title="مرحله یافت نشد" />;

  // ──── Views ────
  if (view === 'intro') {
    return (
      <StepQuizIntro
        quiz={quiz}
        step={step}
        progress={progress}
        canAttempt={canAttempt}
        onStart={handleStartQuiz}
        onBack={() => navigate(-1)}
      />
    );
  }

  if (view === 'taking') {
    return (
      <TakeExam
        examId={quiz.id}
        passThreshold={quiz.passThreshold || 12}
        onSubmitOverride={handleQuizSubmit}
        embeddedMode
      />
    );
  }

  if (view === 'result') {
    return (
      <StepQuizResult
        quiz={quiz}
        step={step}
        progress={progress}
        canAttempt={canAttempt}
        result={lastResult}        
        onBack={() => navigate(-1)}
        onRetry={handleRetry}
      />
    );
  }

  if (view === 'paywall') {
    return (
      <StepQuizPaywall
        quiz={quiz}
        progress={progress}
        canAttempt={canAttempt}
        onPay={handlePayAndUnlock}
        onBack={() => navigate(-1)}
      />
    );
  }

  return null;
}